import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, ElasticFormModal, Label, Tag, TextField } from 'basic-components';
import { AuthUserQuery } from 'dashboard-services/api/auth';
import Query from 'dashboard-services/api/Query';
import { globalActions } from 'primary-components';

import { settingsMyAccountActions } from 'actions/modules/myAccount';
import getApiConfig from 'api/ApiConfig';

import { useMutation } from '@tanstack/react-query';
import { When } from 'react-if';

import './PackagesModal.scss';

const useSaveSettingsCall = ({ onSuccess, getQueryBuilder }) => useMutation({
  ...AuthUserQuery.QUERIES.updateCurrentUserSettings(getQueryBuilder, {},),
  onSuccess
})
const useSaveAsDefault = () => {
  const dispatch = useDispatch(),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => new Query.Builder(getApiConfigLocal()), [getApiConfigLocal]),
        onSuccess = useCallback((_, { settings }) => {
          if(settings.defaultPythonPackages === "") {
            dispatch(globalActions.getMessageStore()).success("Default packages have been reset.")
            dispatch(settingsMyAccountActions.changeSetting(undefined, 'defaultPythonPackages'))
          } else {
            dispatch(globalActions.getMessageStore()).success("Default packages have been saved.")
            dispatch(settingsMyAccountActions.changeSetting(JSON.parse(settings.defaultPythonPackages), 'defaultPythonPackages'))
          }
        }, [dispatch]),
        { mutateAsync: saveSettings } = useSaveSettingsCall({ getQueryBuilder, onSuccess })
  return saveSettings;
}

const parseToPackages = (userPackages, DEFAULT_PACKAGES) => 
  (userPackages.official || DEFAULT_PACKAGES.official)?.map(o => ({ label: o, isOfficial: true }))
    .concat(
      (userPackages.micropip || DEFAULT_PACKAGES.micropip)?.map(m => ({ label: m, isOfficial: false }))
    )
    .sort((a, b) => a.label.localeCompare(b.label))

const SAVE_AS_DEFAULT_STYLE = { marginRight: "16px" }
const PackagesModal = ({ DEFAULT_PACKAGES, userPackages, onClose, onSubmit }) => {
  const [packages, setPackages] = useState(parseToPackages(userPackages, DEFAULT_PACKAGES)),
        [currentPackageText, setCurrentPackageText] = useState(""),
        packagesToSave = useMemo(() => ({
          official: packages.filter(p => p.isOfficial).map(p => p.label),
          micropip: packages.filter(p => !p.isOfficial).map(p => p.label)
        }), [packages]),
        addPackage = useCallback(() => {
          setPackages(a => a.add({ label: currentPackageText, isOfficial: OFFICIAL_PACKAGES.includes(currentPackageText) }))
          setCurrentPackageText("")
        }, [currentPackageText]),
        removePackage = useCallback(pIndex => {
          setPackages(a => a.remove(pIndex))
        }, []),
        onSubmitLocal = useCallback(({ packages = packagesToSave } = {}) => {
          onSubmit(packages)
          onClose()
        }, [onClose, onSubmit, packagesToSave]),
        saveSettings = useSaveAsDefault(),
        saveAsDefault = useCallback(() => 
          saveSettings({ 
            settings: {
              defaultPythonPackages: JSON.stringify(packagesToSave)
            }
          })
          .then(() => onSubmitLocal())
        , [onSubmitLocal, packagesToSave, saveSettings]),
        resetDefaults = useCallback(() => {
          setPackages(parseToPackages(DEFAULT_PACKAGES));
          saveSettings({ 
            settings: {
              defaultPythonPackages: JSON.stringify(DEFAULT_PACKAGES)
            }
          })
          .then(() => onSubmitLocal({ packages: DEFAULT_PACKAGES }))
        }, [DEFAULT_PACKAGES, onSubmitLocal, saveSettings]),
        saveAsDefaultButton = useCallback(() => (
          <Button
              key="Save as default"
              onClick={saveAsDefault}
              size="small"
              style={SAVE_AS_DEFAULT_STYLE}
              type="button"
              variant="secondary"
          >
            Save as default
          </Button>
        ), [saveAsDefault])
  return (
    <ElasticFormModal
        buttons={saveAsDefaultButton}
        className="ng-python-editor__add-packages"
        onClose={onClose}
        onSubmit={onSubmitLocal}
        title="Python packages"
    >
      <div className="ng-python-editor__add-packages__text">
        <span>
          The Python standard library is available without needing to install any packages, 
          <a 
              href="https://docs.python.org/3/library" 
              rel="noopener noreferrer"
              target="_blank" 
          >
            view the full list here.
          </a>
        </span>
      </div>
      <div className="ng-python-editor__add-packages__add">
        <Label>Package name or URL</Label>
        <TextField
            defaultValue={currentPackageText}
            name="currentPackageText"
            onChange={setCurrentPackageText}
        />
        <div className="ng-python-editor__add-packages__add__button">
          <Button
              disabled={!currentPackageText}
              onClick={addPackage}
              type="button"
          >
            Add
          </Button>
        </div>
      </div>
      <div className="ng-python-editor__add-packages__packages">
        <When condition={packages.length > 0}>
          <p>Packages:</p>
        </When>
        {packages.map((p, pIndex) => (
          <Tag 
              key={String(pIndex)}
              onClear={() => removePackage(pIndex)}
          >
            {p?.label}
          </Tag>
        ))}
        <div className="ng-python-editor__add-packages__packages__reset">
          <Button
              key="Reset defaults"
              onClick={resetDefaults}
              size="small"
              style={SAVE_AS_DEFAULT_STYLE}
              type="button"
              variant="text"
          >
            Reset to defaults
          </Button>
        </div>
      </div>
    </ElasticFormModal>
  )
}

PackagesModal.propTypes = {
  DEFAULT_PACKAGES: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  userPackages: PropTypes.object.isRequired,
}

export default PackagesModal;

const OFFICIAL_PACKAGES = [
  "affine",
  "aiohappyeyeballs",
  "aiohttp",
  "aiosignal",
  "altair",
  "annotated-types",
  "anyio",
  "apsw",
  "argon2-cffi",
  "argon2-cffi-bindings",
  "asciitree",
  "astropy",
  "astropy_iers_data",
  "asttokens",
  "async-timeout",
  "atomicwrites",
  "attrs",
  "audioop-lts",
  "autograd",
  "awkward-cpp",
  "b2d",
  "bcrypt",
  "beautifulsoup4",
  "bilby.cython",
  "biopython",
  "bitarray",
  "bitstring",
  "bleach",
  "blosc2",
  "bokeh",
  "boost-histogram",
  "Bottleneck",
  "brotli",
  "cachetools",
  "Cartopy",
  "casadi",
  "cbor-diag",
  "certifi",
  "cffi",
  "cffi_example",
  "cftime",
  "charset-normalizer",
  "clarabel",
  "click",
  "cligj",
  "clingo",
  "cloudpickle",
  "cmyt",
  "cobs",
  "colorspacious",
  "contourpy",
  "coolprop",
  "coverage",
  "cramjam",
  "crc32c",
  "cryptography",
  "css-inline",
  "cssselect",
  "cvxpy-base",
  "cycler",
  "cysignals",
  "cytoolz",
  "decorator",
  "demes",
  "deprecation",
  "diskcache",
  "distlib",
  "distro",
  "docutils",
  "donfig",
  "ewah_bool_utils",
  "exceptiongroup",
  "executing",
  "fastapi",
  "fastcan",
  "fastparquet",
  "fiona",
  "fonttools",
  "freesasa",
  "frozenlist",
  "fsspec",
  "future",
  "galpy",
  "geopandas",
  "gmpy2",
  "google-crc32c",
  "gsw",
  "h11",
  "h3",
  "h5py",
  "healpy",
  "highspy",
  "html5lib",
  "httpcore",
  "httpx",
  "idna",
  "igraph",
  "imageio",
  "imgui-bundle",
  "iminuit",
  "iniconfig",
  "inspice",
  "ipython",
  "jedi",
  "Jinja2",
  "jiter",
  "joblib",
  "jsonpatch",
  "jsonpointer",
  "jsonschema",
  "jsonschema_specifications",
  "kiwisolver",
  "lakers-python",
  "lazy_loader",
  "lazy-object-proxy",
  "libcst",
  "lightgbm",
  "logbook",
  "lxml",
  "lz4",
  "MarkupSafe",
  "matplotlib",
  "matplotlib-inline",
  "memory-allocator",
  "micropip",
  "ml_dtypes",
  "mmh3",
  "more-itertools",
  "mpmath",
  "msgpack",
  "msgspec",
  "msprime",
  "multidict",
  "munch",
  "mypy",
  "narwhals",
  "ndindex",
  "netcdf4",
  "networkx",
  "newick",
  "nh3",
  "nlopt",
  "nltk",
  "numcodecs",
  "numpy",
  "openai",
  "opencv-python",
  "optlang",
  "orjson",
  "packaging",
  "pandas",
  "parso",
  "patsy",
  "pcodec",
  "peewee",
  "pi-heif",
  "Pillow",
  "pillow-heif",
  "pkgconfig",
  "platformdirs",
  "pluggy",
  "ply",
  "pplpy",
  "primecountpy",
  "prompt_toolkit",
  "propcache",
  "protobuf",
  "pure-eval",
  "py",
  "pyarrow",
  "pycdfpp",
  "pyclipper",
  "pycparser",
  "pycryptodome",
  "pydantic",
  "pydantic_core",
  "pyerfa",
  "pygame-ce",
  "Pygments",
  "pyheif",
  "pyiceberg",
  "pyinstrument",
  "pylimer-tools",
  "PyMuPDF",
  "pynacl",
  "pyodide-http",
  "pyodide-unix-timezones",
  "pyparsing",
  "pyproj",
  "pyrodigal",
  "pyrsistent",
  "pysam",
  "pyshp",
  "pytaglib",
  "pytest",
  "pytest-asyncio",
  "pytest-benchmark",
  "pytest_httpx",
  "python-calamine",
  "python-dateutil",
  "python-flint",
  "python-magic",
  "python-sat",
  "python-solvespace",
  "pytz",
  "pywavelets",
  "pyxel",
  "pyxirr",
  "pyyaml",
  "rasterio",
  "rateslib",
  "rebound",
  "reboundx",
  "referencing",
  "regex",
  "requests",
  "retrying",
  "rich",
  "river",
  "RobotRaconteur",
  "rpds-py",
  "ruamel.yaml",
  "rustworkx",
  "scikit-image",
  "scikit-learn",
  "scipy",
  "screed",
  "setuptools",
  "shapely",
  "simplejson",
  "sisl",
  "six",
  "smart-open",
  "sniffio",
  "sortedcontainers",
  "soundfile",
  "soupsieve",
  "sourmash",
  "soxr",
  "sparseqr",
  "sqlalchemy",
  "stack-data",
  "starlette",
  "statsmodels",
  "strictyaml",
  "svgwrite",
  "swiglpk",
  "sympy",
  "tblib",
  "termcolor",
  "texttable",
  "texture2ddecoder",
  "threadpoolctl",
  "tiktoken",
  "tomli",
  "tomli-w",
  "toolz",
  "tqdm",
  "traitlets",
  "traits",
  "tree-sitter",
  "tree-sitter-go",
  "tree-sitter-java",
  "tree-sitter-python",
  "tskit",
  "typing-extensions",
  "typing-inspection",
  "tzdata",
  "ujson",
  "uncertainties",
  "unyt",
  "urllib3",
  "vega-datasets",
  "vrplib",
  "wcwidth",
  "webencodings",
  "wordcloud",
  "wrapt",
  "xarray",
  "xgboost",
  "xlrd",
  "xxhash",
  "xyzservices",
  "yarl",
  "yt",
  "zengl",
  "zfpy",
  "zstandard",
];